import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./LinkClinic.css";
import websiteLinkIcon from "../../../assets/icons/svg/linkclinic-website-link.svg";
import carouselPrevIcon from "../../../assets/icons/svg/linkclinic-carousel-prev.svg";
import carouselNextIcon from "../../../assets/icons/svg/linkclinic-carousel-next.svg";
import contactPhoneIcon from "../../../assets/icons/svg/linkclinic-contact-phone.svg";
import contactEmailIcon from "../../../assets/icons/svg/linkclinic-contact-email.svg";
import contactLocationIcon from "../../../assets/icons/svg/linkclinic-contact-location.svg";
import contactHoursIcon from "../../../assets/icons/svg/linkclinic-contact-hours.svg";

// Fallback logos si une clinique n'en a pas
import { CLINICS_DATA, LOGO_FALLBACKS } from "../../fakedata/clinics";

const LinkClinic = ({ clinicData }) => {
	const { t } = useTranslation();
  // Si aucun clinicData passé, on prend la première du fakedata
  const clinic = clinicData || CLINICS_DATA[0];

  // Logo fallback déterministe (si logo null)
  const logoSrc = useMemo(() => {
    if (clinic.logo) return clinic.logo;
    if (!clinic.name) return LOGO_FALLBACKS[0];
    const sum = Array.from(clinic.name).reduce((s, ch) => s + ch.charCodeAt(0), 0);
    return LOGO_FALLBACKS[sum % LOGO_FALLBACKS.length];
  }, [clinic.logo, clinic.name]);

  // ✅ Deux images obligatoires via fakedata
  const images = useMemo(() => {
    const arr = Array.isArray(clinic.images) ? clinic.images.filter(Boolean) : [];
    // on sécurise quand même pour éviter un crash si arr < 2
    if (arr.length >= 2) return [arr[0], arr[1]];
    return arr.length === 1 ? [arr[0], arr[0]] : LOGO_FALLBACKS;
  }, [clinic.images]);

  // Carrousel à 2 positions
  const [idx, setIdx] = useState(0);
  const prev = () => setIdx((i) => (i === 0 ? 1 : 0));
  const next = () => setIdx((i) => (i === 0 ? 1 : 0));

  const handleViewWebsite = () => {
    if (clinic.website) window.open(clinic.website, "_blank", "noopener,noreferrer");
  };

  const getEmbedMapUrl = (url) => {
    if (!url) return null;
    try {
      const u = new URL(url);
      const q = u.searchParams.get("q");
      if (q) return `https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed`;
      if (url.includes("/embed") || url.includes("output=embed")) return url;
      return `https://www.google.com/maps?${u.searchParams.toString()}&output=embed`;
    } catch {
      return url;
    }
  };

  return (
    <div className="link-clinic-container">
      {/* Header */}
      <div className="lc-header">
        <div className="lc-header-left">
          {logoSrc ? (
            <img src={logoSrc} alt={clinic.name} className="lc-logo" />
          ) : (
            <div className="lc-logo-placeholder" />
          )}
          <div className="lc-header-info">
            <h1 className="lc-clinic-name">{clinic.name}</h1>
            <p className="lc-clinic-type">{clinic.type}</p>
          </div>
        </div>

	        <button className="lc-website-btn" onClick={handleViewWebsite} type="button">
          {/* Icône lien */}
	          <img src={websiteLinkIcon} width="24" height="24" alt="" aria-hidden="true" />
	          {t("locators.clinic.viewWebsite")}
        </button>
      </div>

      {/* Why choose */}
      <section className="lc-section lc-why-choose">
        <h2 className="lc-section-title">{clinic.whyChoose?.title}</h2>
        <p className="lc-section-description">{clinic.whyChoose?.description}</p>
      </section>

      {/* Carousel (2 images) */}
      <section className="lc-carousel-section">
        <div className="lc-carousel">
          <button className="lc-arrow lc-prev" onClick={prev} type="button" aria-label="Previous image">
            <img src={carouselPrevIcon} width="24" height="24" alt="" aria-hidden="true" />
          </button>

          <div className="lc-carousel-viewport">
            <img
              src={images[idx]}
              alt={`${clinic.name} - image ${idx + 1}`}
              className="lc-carousel-image"
              draggable={false}
            />
          </div>

          <button className="lc-arrow lc-next" onClick={next} type="button" aria-label="Next image">
            <img src={carouselNextIcon} width="24" height="14" alt="" aria-hidden="true" />
          </button>
        </div>
      </section>

      {/* Services */}
      <section className="lc-section lc-services">
        <h2 className="lc-section-title">{clinic.offeredServices?.title}</h2>
        <p className="lc-section-description">{clinic.offeredServices?.description}</p>
        <div className="lc-services-grid">
          {(clinic.offeredServices?.services || []).map((s, i) => (
            <div key={i} className="lc-service-card">{s}</div>
          ))}
        </div>
      </section>

      {/* Contacts */}
      <section className="lc-section lc-contacts">
        <div className="lc-contacts-content">
          <div className="lc-contact-info">
            <h2 className="lc-section-Contact">{clinic.contacts?.title}</h2>
            <p className="lc-section-descriptionContact">{clinic.contacts?.description}</p>

            <div className="lc-contact-item">
              <img src={contactPhoneIcon} width="18" height="18" alt="" aria-hidden="true" />
              <span className="lc-contact-text">{clinic.contacts?.phone}</span>
            </div>

            <div className="lc-contact-item">
              <img src={contactEmailIcon} width="18" height="18" alt="" aria-hidden="true" />
              <span className="lc-contact-text">{clinic.contacts?.email}</span>
            </div>

            <div className="lc-contact-item">
              <img src={contactLocationIcon} width="18" height="18" alt="" aria-hidden="true" />
              <span className="lc-contact-text">{clinic.contacts?.address}</span>
            </div>

            <div className="lc-contact-item lc-hours">
              <img src={contactHoursIcon} width="18" height="18" alt="" aria-hidden="true" />
              <span className="lc-contact-text lc-hours-text">{clinic.contacts?.hours}</span>
            </div>
          </div>

          <div className="lc-map-container">
            {clinic.contacts?.mapUrl ? (
              <iframe
                src={getEmbedMapUrl(clinic.contacts.mapUrl)}
                title={`${clinic.name} location`}
                className="lc-map-iframe"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : (
              <div className="lc-map-placeholder">
                <p className="lc-map-text">No map available</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LinkClinic;